// RatingStar — Phase 4 Plan 04-04 Task 2 (FWD-04 UI-SPEC §Screen 9).
//
// 5-button group with accent fill on filled stars. Tap-to-set rating; tap
// the currently-set star to clear (set rating to null).
//
// Accessibility (UI-SPEC §11.3):
//   - role="radiogroup" aria-label="Carrier rating"
//   - each button has aria-label="Rate N stars"
//   - keyboard nav: tab through buttons; Enter/Space toggles
//
// Read-only mode (readOnly=true): renders stars without buttons; used in
// list cell to show carrier.rating.

import { useTranslation } from "react-i18next";

export interface RatingStarProps {
  value: number | null;
  onChange?: (next: number | null) => void;
  readOnly?: boolean;
  size?: number;
}

const FILL_ACCENT = "var(--tg-theme-button-color, #4f46e5)";
const FILL_EMPTY = "var(--tg-theme-hint-color, #d1d5db)";

export function RatingStar({
  value,
  onChange,
  readOnly = false,
  size = 24,
}: RatingStarProps): React.JSX.Element {
  const { t } = useTranslation();
  const safeValue = value ?? 0;

  const handleClick = (n: number): void => {
    if (readOnly || !onChange) return;
    // Tap the currently-set N → clear to null.
    if (n === value) {
      onChange(null);
      return;
    }
    onChange(n);
  };

  const ariaGroup = t("dispatcher:carriers.rating_group", "Carrier rating");

  return (
    <div
      role="radiogroup"
      aria-label={ariaGroup}
      data-testid="rating-star-group"
      style={{
        display: "inline-flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= safeValue;
        const star = filled ? "★" : "☆";
        const color = filled ? FILL_ACCENT : FILL_EMPTY;
        const ariaLabel = t("dispatcher:carriers.rate_n_stars", "Rate {{n}} stars", { n });
        if (readOnly) {
          return (
            <span
              key={n}
              data-testid={`rating-star-${n}${filled ? "-filled" : ""}`}
              aria-label={ariaLabel}
              role="radio"
              aria-checked={filled}
              style={{
                color,
                fontSize: size,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              {star}
            </span>
          );
        }
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={filled}
            aria-label={ariaLabel}
            data-testid={`rating-star-${n}`}
            onClick={() => handleClick(n)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color,
              fontSize: size,
              lineHeight: 1,
              userSelect: "none",
              minWidth: 32,
              minHeight: 32,
            }}
          >
            {star}
          </button>
        );
      })}
    </div>
  );
}
